<script lang="ts">
  import { createEventDispatcher, onMount } from "svelte";

  /**
   * A miniature of the real chat stage for the landing page: sky backdrop, white
   * coach bubble, emerald starter chips, the visitor's reply, and one plan item.
   * Same classes as the chat transcript so it stays honest about what the
   * visitor will see. The starter chips are real — they dispatch `start`.
   */
  export let starters: string[] = [];
  /** The coach's opening line; a tenant passes the welcome its learners actually see. */
  export let greeting = "Hi — what would you like help with today?";
  /** Play the scripted reply and plan item after the greeting; off shows the opening turn alone. */
  export let exchange = true;
  /** The starter currently being started, so only that chip shows selected/busy. */
  export let activeStarter = "";
  export let disabled = false;
  export let status = "";
  export let statusIsError = false;

  const dispatch = createEventDispatcher<{ start: string }>();

  // The exchange plays once when it scrolls into view: coach, visitor, coach,
  // then the plan item ticks. Turns are visible by default so a failed script
  // or reduced motion still shows the finished state.
  let stage: HTMLElement | undefined;
  let armed = false;
  let played = false;

  onMount(() => {
    if (!stage) return;
    if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const element = stage;
    const inView = () => {
      const rect = element.getBoundingClientRect();
      return rect.top < window.innerHeight * 0.95 && rect.bottom > 0;
    };
    const play = () => {
      played = true;
      cleanup();
    };
    // IntersectionObserver is the primary trigger; the scroll/visibility checks
    // cover a page that loads in a background tab, where observers stay silent.
    const observer =
      typeof IntersectionObserver === "function"
        ? new IntersectionObserver((entries) => entries.some((entry) => entry.isIntersecting) && play(), {
            threshold: 0.15,
          })
        : null;
    const check = () => {
      if (document.visibilityState === "visible" && inView()) play();
    };
    const cleanup = () => {
      observer?.disconnect();
      window.removeEventListener("scroll", check);
      document.removeEventListener("visibilitychange", check);
    };
    armed = true;
    observer?.observe(element);
    window.addEventListener("scroll", check, { passive: true });
    document.addEventListener("visibilitychange", check);
    check();
    return cleanup;
  });
</script>

<div
  bind:this={stage}
  class="sample-stage stage-shadow rounded-3xl bg-white p-4 sm:p-6"
  class:is-armed={armed}
  class:is-played={played}
  aria-labelledby="sample-heading">
  <h2 id="sample-heading" class="sr-only">What the coach looks like</h2>
  <div class="flex flex-col gap-3">
    <div class="turn flex justify-start" style="--turn: 0">
      <div class="max-w-[92%] rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        <p>{greeting}</p>
        <div class="mt-3 flex flex-wrap gap-2">
          {#each starters as starter}
            <button
              type="button"
              class="min-h-11 max-w-full rounded-full border px-4 py-2 text-left text-sm font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700 disabled:cursor-not-allowed {activeStarter ===
              starter
                ? 'border-emerald-300 bg-emerald-50'
                : 'border-emerald-200 bg-white disabled:opacity-60'}"
              on:click={() => dispatch("start", starter)}
              {disabled}
              aria-pressed={activeStarter === starter}
              aria-busy={activeStarter === starter && disabled}>
              {starter}
            </button>
          {/each}
        </div>
      </div>
    </div>
    {#if exchange}
      <div class="turn flex justify-end" style="--turn: 1">
        <div class="max-w-[85%] rounded-2xl bg-slate-700 px-4 py-3 text-sm text-white">
          I like being outdoors and fixing things
        </div>
      </div>
      <div class="turn flex justify-start" style="--turn: 2">
        <div class="max-w-[92%] rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <p>Two routes fit that. Want to compare them, or add one to your plan?</p>
          <div class="mt-3 rounded-lg bg-white px-3 py-2.5">
            <p class="text-xs font-semibold text-slate-500">Your action plan</p>
            <p class="mt-1 flex items-start gap-2 text-sm text-slate-900">
              <svg aria-hidden="true" viewBox="0 0 20 20" class="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" fill="none">
                <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="1.5" />
                <path
                  class="tick"
                  pathLength="1"
                  d="M6.5 10.5l2.5 2.5 4.5-5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
              Look at a solar installer apprenticeship near me
            </p>
          </div>
        </div>
      </div>
    {/if}
  </div>
  <p
    class="text-sm font-medium {status ? 'mt-3' : ''} {statusIsError ? 'text-rose-700' : 'text-slate-900'}"
    role="status"
    aria-live="polite">
    {status}
  </p>
</div>

<style>
  /* DESIGN.md Stage shadow: the one floating surface on the page. */
  .stage-shadow {
    box-shadow:
      0 20px 25px -5px rgb(0 0 0 / 0.1),
      0 8px 10px -6px rgb(0 0 0 / 0.1);
  }

  /* Playback. Armed only by script and only without reduced motion; each turn
     waits its own beat, the way real replies arrive. */
  .sample-stage.is-armed .turn {
    opacity: 0;
    transform: translateY(10px);
  }

  .sample-stage.is-armed.is-played .turn {
    animation: turn-arrive 420ms cubic-bezier(0.16, 1, 0.3, 1) both;
    animation-delay: calc(var(--turn) * 700ms);
  }

  @keyframes turn-arrive {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .sample-stage.is-armed .tick {
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
  }

  .sample-stage.is-armed.is-played .tick {
    animation: tick-draw 320ms cubic-bezier(0.16, 1, 0.3, 1) both;
    animation-delay: calc(2 * 700ms + 420ms + 150ms);
  }

  @keyframes tick-draw {
    to {
      stroke-dashoffset: 0;
    }
  }
</style>
